import { customAxios } from 'api/orval-mutator';
import {
  downloadAsset,
  getAssetById,
  getAssetClientUsers,
  updateAssetClientUserAccess,
} from 'api/generated/asset-controller/asset-controller';
import { downloadUserAsset } from 'api/generated/user-asset-controller/user-asset-controller';
import type { AssetClientUserUpdateRequest } from 'api/generated/schemas/assetClientUserUpdateRequest';
import type { AssetDto } from 'api/generated/schemas/assetDto';
import type { UserAccessDto } from 'api/generated/schemas/userAccessDto';

/**
 * Spring MVC binds criteria fields as flat query params ({@code viewed.equals}). The generated
 * OpenAPI types nest under {@code criteria}, which axios would serialize incorrectly.
 */

export const fetchUnreadUserAssetsCount = (userId: string): Promise<number> =>
  customAxios<number>({
    url: `/api/${userId}/user-assets/count`,
    method: 'GET',
    params: { 'viewed.equals': false },
  });

export interface FetchArchivedAssetsCountArgs {
  userId: string;
  clientId: string | number;
  search?: string;
}

export const fetchArchivedAssetsCount = ({
  userId,
  clientId,
  search,
}: FetchArchivedAssetsCountArgs): Promise<number> =>
  customAxios<number>({
    url: `/api/${userId}/clients/${Number(clientId)}/assets/count`,
    method: 'GET',
    params: {
      'status.equals': 'ARCHIVED',
      ...(search ? { q: search } : {}),
    },
  });

/** Matches legacy {@link adminUploadClientFile}'s multipart shape: JSON part as {@link Blob} with {@code application/json}. */
export interface AdminUploadClientFileArgs {
  oktaUserId: string;
  clientId: string | number;
  fileName: { value: string };
  file: Blob | File;
  description?: string;
}

export const adminUploadClientFile = ({
  oktaUserId,
  clientId,
  fileName,
  file,
  description,
}: AdminUploadClientFileArgs): Promise<AssetDto> => {
  const formData = new FormData();

  formData.append('file', file);
  formData.append(
    'asset',
    new Blob(
      [
        JSON.stringify({
          name: fileName.value,
          description,
        }),
      ],
      { type: 'application/json' }
    )
  );

  return customAxios<AssetDto>({
    url: `/api/${oktaUserId}/clients/${Number(clientId)}/assets`,
    method: 'POST',
    data: formData,
  });
};

export interface AdminPatchClientFileArgs {
  oktaUserId: string;
  clientId: string | number;
  fileId: string | number;
  fileName?: { value: string };
  file?: (Blob | File) & { isFakeFile?: boolean };
  description?: string;
  status?: string;
  autoAssignToUsers?: boolean;
}

export const adminPatchClientFile = ({
  oktaUserId,
  clientId,
  fileId,
  fileName,
  file,
  description,
  status,
  autoAssignToUsers,
}: AdminPatchClientFileArgs): Promise<AssetDto> => {
  const formData = new FormData();

  if (file && !file.isFakeFile) {
    formData.append('file', file);
  }

  formData.append(
    'asset',
    new Blob(
      [
        JSON.stringify({
          name: fileName?.value,
          description,
          status,
          autoAssignToUsers,
        }),
      ],
      { type: 'application/json' }
    )
  );

  return customAxios<AssetDto>({
    url: `/api/${oktaUserId}/clients/${Number(clientId)}/assets/${Number(
      fileId
    )}`,
    method: 'PATCH',
    data: formData,
  });
};

export interface LookupClientFileArgs {
  userId: string;
  clientId: string | number;
  fileId: string | number;
}

export const fetchClientAssetById = ({
  userId,
  clientId,
  fileId,
}: LookupClientFileArgs): Promise<AssetDto> =>
  getAssetById(userId, Number(clientId), Number(fileId));

export const fetchClientAssetUsers = ({
  userId,
  clientId,
  fileId,
}: LookupClientFileArgs): Promise<UserAccessDto[]> =>
  getAssetClientUsers(userId, Number(clientId), Number(fileId));

export interface PatchClientAssetUsersArgs {
  oktaUserId: string;
  clientId: string | number;
  fileId: string | number;
  changedUsers: AssetClientUserUpdateRequest[];
}

export const patchClientAssetUsersAccess = ({
  oktaUserId,
  clientId,
  fileId,
  changedUsers,
}: PatchClientAssetUsersArgs): Promise<UserAccessDto[]> =>
  updateAssetClientUserAccess(
    oktaUserId,
    Number(clientId),
    Number(fileId),
    changedUsers
  );

export interface DownloadClientOrUserAssetArgs {
  currentUserOktaId: string;
  clientId?: string | number;
  fileId: string | number;
}

export const downloadMarketViewAssetBlob = async ({
  currentUserOktaId,
  clientId,
  fileId,
}: DownloadClientOrUserAssetArgs): Promise<Blob> => {
  const blobOpts = { responseType: 'blob' as const };

  if (clientId !== undefined && clientId !== '') {
    const response = await downloadAsset(
      currentUserOktaId,
      Number(clientId),
      Number(fileId),
      blobOpts
    );

    return response.data;
  }

  const response = await downloadUserAsset(
    currentUserOktaId,
    Number(fileId),
    blobOpts
  );

  return response.data;
};
